import type { MeetingParticipant, MeetingRecord, MeetingRsvpStatus } from '../../types/meetings'

export const DRAFT_MEETING_DROP_TARGET_ID = '__meeting_draft__'

/**
 * Extract the list of internal participant uids from a saved meeting.
 * External invitees are excluded.
 */
export const savedParticipantIds = (meeting: MeetingRecord | null): string[] =>
  (meeting?.participants ?? []).flatMap((participant) => participant.external ? [] : [participant.userId])

/**
 * Return the saved internal participant's RSVP status for a given uid.
 * Returns null if the meeting is null, the uid is empty, or the participant is not
 * a saved internal participant (e.g. an unsaved draft addition or an external invitee).
 */
export const participantRsvpStatus = (
  meeting: MeetingRecord | null,
  uid: string,
): MeetingRsvpStatus | null => {
  if (!meeting || !uid) return null
  const participant = (meeting.participants ?? []).find(
    (p): p is MeetingParticipant => !p.external && p.userId === uid,
  )
  return participant?.rsvpStatus ?? null
}

/**
 * A snapshot of the caller's ephemeral drag/draft state, used to derive
 * what each meeting element should render on the calendar.
 */
export interface EffectiveParticipantContext {
  selectedMeetingId: string | null
  draftParticipantIds: string[]
  draggingMemberUid: string | null
  dragHoverMeetingId: string | null
}

/**
 * getEffectiveParticipantIds — what uids should appear on this meeting's
 * calendar bar / pill / block right now.
 *
 * Rules, in order:
 * 1. If the meeting is the selected one, prefer draft.participantIds so
 *    drag-added members appear immediately on the calendar even before
 *    Save persists the change.
 * 2. If a member is currently being dragged AND this meeting is the
 *    hover target, append the dragging uid at the end so a ghost avatar
 *    can preview the drop.
 * 3. Otherwise fall back to the saved participants on the meeting.
 */
export const getEffectiveParticipantIds = (
  meeting: MeetingRecord | null,
  context: EffectiveParticipantContext,
): string[] => {
  if (!meeting) return []
  const base = meeting.id === context.selectedMeetingId
    ? context.draftParticipantIds
    : savedParticipantIds(meeting)
  if (
    context.draggingMemberUid &&
    context.dragHoverMeetingId === meeting.id &&
    !base.includes(context.draggingMemberUid)
  ) {
    return [...base, context.draggingMemberUid]
  }
  return base
}

/**
 * getPendingParticipantIds — the uids that exist in the current draft but
 * not yet persisted to the saved meeting. Used to render the "unsaved"
 * badge on the sidecar chip and dashed outline on the bar avatar.
 * Only meaningful for the selected meeting.
 */
export const getPendingParticipantIds = (
  meeting: MeetingRecord | null,
  context: Pick<EffectiveParticipantContext, 'selectedMeetingId' | 'draftParticipantIds'>,
): Set<string> => {
  if (!meeting || meeting.id !== context.selectedMeetingId) return new Set<string>()
  const saved = new Set(savedParticipantIds(meeting))
  return new Set(context.draftParticipantIds.filter((uid) => !saved.has(uid)))
}

/**
 * isPhantomAvatarFor — true when a given uid on a given meeting should
 * render as a phantom (transient ghost during drag-over that hasn't been
 * dropped yet, so it isn't in the saved list or the draft list).
 */
export const isPhantomAvatarFor = (
  meeting: MeetingRecord | null,
  uid: string,
  context: EffectiveParticipantContext,
): boolean => {
  if (!meeting) return false
  if (context.draggingMemberUid !== uid) return false
  if (context.dragHoverMeetingId !== meeting.id) return false
  const saved = savedParticipantIds(meeting)
  if (saved.includes(uid)) return false
  if (context.draftParticipantIds.includes(uid)) return false
  return true
}

export interface DraftMeetingDropContext {
  createOpen: boolean
  selectedMeetingId: string | null
  draftMeetingId: string | null
}

export const isDraftMeetingDropTarget = (
  meetingId: string | null,
  context: Pick<DraftMeetingDropContext, 'createOpen' | 'draftMeetingId'>,
): boolean => meetingId === DRAFT_MEETING_DROP_TARGET_ID && context.createOpen && !context.draftMeetingId

export const shouldApplyParticipantDropToCurrentDraft = (
  meetingId: string | null,
  context: DraftMeetingDropContext,
): boolean => {
  if (!meetingId) return false
  if (isDraftMeetingDropTarget(meetingId, context)) return true
  return Boolean(context.draftMeetingId && meetingId === context.draftMeetingId && context.selectedMeetingId === meetingId)
}

export const getEffectiveDraftParticipantIds = (
  context: Pick<EffectiveParticipantContext, 'draftParticipantIds' | 'draggingMemberUid' | 'dragHoverMeetingId'>,
): string[] => {
  const base = context.draftParticipantIds
  if (
    context.draggingMemberUid &&
    context.dragHoverMeetingId === DRAFT_MEETING_DROP_TARGET_ID &&
    !base.includes(context.draggingMemberUid)
  ) {
    return [...base, context.draggingMemberUid]
  }
  return base
}

export const isPhantomDraftAvatarFor = (
  uid: string,
  context: Pick<EffectiveParticipantContext, 'draftParticipantIds' | 'draggingMemberUid' | 'dragHoverMeetingId'>,
): boolean => Boolean(
  context.draggingMemberUid === uid &&
  context.dragHoverMeetingId === DRAFT_MEETING_DROP_TARGET_ID &&
  !context.draftParticipantIds.includes(uid),
)

export interface RosterClickTargetContext {
  createOpen: boolean
  draftMeetingId?: string | null
  selectedMeetingId?: string | null
}

export const rosterClickTargetId = ({
  createOpen,
  draftMeetingId,
  selectedMeetingId,
}: RosterClickTargetContext): string | null => {
  if (createOpen && !draftMeetingId) {
    return DRAFT_MEETING_DROP_TARGET_ID
  }
  return selectedMeetingId || null
}
